import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Calendar } from 'lucide-react';
import { toast } from 'sonner';

const Schedule = () => {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);

  useEffect(() => {
    loadSchedules();
    loadUser();
  }, []);

  const loadUser = async () => {
    try {
      const res = await axios.get('/auth/me');
      setUser(res.data);
    } catch (error) {
      console.error('Error loading user:', error);
    }
  };

  const loadSchedules = async () => {
    try {
      const res = await axios.get('/schedules');
      setSchedules(res.data);
    } catch (error) {
      console.error('Error loading schedules:', error);
      toast.error('Fehler beim Laden der Arbeitspläne');
    } finally {
      setLoading(false);
    }
  };

  const getCurrentWeek = () => {
    const now = new Date();
    const start = new Date(now);
    start.setDate(now.getDate() - now.getDay() + 1); // Monday
    return start;
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit' });
  };

  const getWeekDates = (weekStart) => {
    const dates = [];
    const start = new Date(weekStart);
    for (let i = 0; i < 7; i++) {
      const date = new Date(start);
      date.setDate(start.getDate() + i);
      dates.push(date);
    }
    return dates;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="schedule-page">
      <div>
        <h1 className="text-4xl md:text-5xl font-black tracking-tight uppercase text-white mb-2">
          Arbeitsplan
        </h1>
        <p className="text-zinc-400">Wöchentlicher Dienstplan</p>
      </div>

      {schedules.length > 0 ? (
        <div className="space-y-6">
          {schedules.map(schedule => {
            const weekDates = getWeekDates(schedule.week_start);
            
            return (
              <Card key={schedule.schedule_id} className="bg-zinc-900 border-zinc-800" data-testid={`schedule-${schedule.schedule_id}`}>
                <CardHeader>
                  <CardTitle className="text-xl font-bold text-white">
                    Woche vom {formatDate(schedule.week_start)}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-7 gap-4">
                    {weekDates.map((date, index) => {
                      const shift = schedule.shifts.find(s => new Date(s.date).toDateString() === date.toDateString());
                      
                      return (
                        <div 
                          key={index} 
                          className="p-4 rounded-lg bg-zinc-800 border border-zinc-700"
                        >
                          <div className="text-xs font-bold text-zinc-400 mb-2 uppercase">
                            {date.toLocaleDateString('de-DE', { weekday: 'short' })}
                          </div>
                          <div className="text-sm text-zinc-300 mb-3">
                            {date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}
                          </div>
                          {shift ? (
                            <div className="space-y-1">
                              <div className="text-sm font-medium text-white">
                                {shift.start_time} - {shift.end_time}
                              </div>
                              {shift.location && (
                                <div className="text-xs text-zinc-400">
                                  {shift.location}
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="text-xs text-zinc-600">Frei</div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Calendar className="h-16 w-16 text-zinc-600 mb-4" />
            <p className="text-zinc-400 text-center">Keine Arbeitspläne verfügbar</p>
            <p className="text-sm text-zinc-600 mt-2">
              {user?.role === 'admin' 
                ? 'Erstellen Sie einen neuen Arbeitsplan für Ihre Mitarbeiter'
                : 'Ihr Administrator hat noch keinen Arbeitsplan erstellt'}
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Schedule;
